// src/pages/CategoryMeals.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PackageItem from '../Components/PackageItem';
import './CategoryMeals.css';

const CategoryMeals = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();

    const [category, setCategory] = useState(null);
    const [allPackages, setAllPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bannerUrl, setBannerUrl] = useState(null);

    const [isPureVeg, setIsPureVeg] = useState(false);
    const [activeMealType, setActiveMealType] = useState('breakfast');
    const [selectedMeals, setSelectedMeals] = useState({
        breakfast: null,
        lunch: null,
        dinner: null,
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const categoryRef = doc(db, 'categories', categoryId);
                const categorySnap = await getDoc(categoryRef);
                if (!categorySnap.exists()) throw new Error("Category not found");
                const categoryData = categorySnap.data();
                setCategory(categoryData);

                const bannerCollectionMap = {
                    'Home Meal': 'home_meal_banners',
                    'Diet Plan': 'diet_meal_banners',
                };
                const bannerCollectionName = bannerCollectionMap[categoryData.name];

                if (bannerCollectionName) {
                    const bannerQuery = query(collection(db, bannerCollectionName));
                    const bannerSnapshot = await getDocs(bannerQuery);
                    if (!bannerSnapshot.empty) {
                        const bannerData = bannerSnapshot.docs[0].data();
                        setBannerUrl(bannerData.imageUrl);
                    }
                }

                const packagesQuery = query(collection(db, 'packages'), where('category', '==', categoryId));
                const packagesSnapshot = await getDocs(packagesQuery);
                
                const packagesData = packagesSnapshot.docs.map(pkgDoc => {
                    const pkg = { id: pkgDoc.id, ...pkgDoc.data() };
                    const menuObject = {};
                    if (pkg.weeklyMenu && Array.isArray(pkg.weeklyMenu)) {
                        pkg.weeklyMenu.forEach(item => {
                            if (item.dayName && item.meal && item.meal.name) {
                                menuObject[item.dayName] = item.meal.name;
                            }
                        });
                    }
                    return { ...pkg, weeklyMenu: menuObject }; 
                });

                setAllPackages(packagesData);

            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to load meal plans.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [categoryId]);

    const handleSelectPackage = (pkg) => {
        setSelectedMeals(prev => ({
            ...prev,
            [pkg.mealType]: prev[pkg.mealType]?.id === pkg.id ? null : pkg,
        }));
    };
    
    const subtotal = Object.values(selectedMeals).reduce((acc, curr) => {
        return acc + (curr ? (curr.price || 0) : 0);
    }, 0);

    const handleSubscribe = () => {
        if (Object.values(selectedMeals).every(m => m === null)) {
            alert("Please select at least one meal package.");
            return;
        }
        navigate(`/category/${categoryId}/review`, {
            state: {
                packageData: category,
                selectedMeals,
                subtotal: Math.round(subtotal)
            }
        });
    };

    if (loading) return <div className="meal-page-status">Loading plans...</div>;
    if (error) return <div className="meal-page-status error">{error}</div>;

    const filteredPackages = allPackages.filter(pkg => 
        pkg.mealType === activeMealType && (!isPureVeg || pkg.isPureVeg)
    );
    
    const selectionCounts = {
        breakfast: selectedMeals.breakfast ? 1 : 0,
        lunch: selectedMeals.lunch ? 1 : 0,
        dinner: selectedMeals.dinner ? 1 : 0,
    };

    return (
        <div className="category-meals-page">
            {/* ✅ BANNER WITH BACKGROUND IMAGE APPROACH FOR BETTER CONTROL */}
            {bannerUrl && (
                <div 
                    className="category-banner"
                    style={{ backgroundImage: `url(${bannerUrl})` }}
                ></div>
            )}

            <div className="category-content-grid">
                <div className="page-main-content">
                    <Link to="/package" className="back-link">← Back to Cuisines</Link>
                    <h1 className="category-title">{category?.name}</h1>
                    <p className="category-subtitle">Pick your customized combo with meal time</p>

                    <div className="veg-toggle-container">
                        <label className="switch">
                            <input type="checkbox" checked={isPureVeg} onChange={(e) => setIsPureVeg(e.target.checked)} />
                            <span className="slider round"></span>
                        </label>
                        <span>Pure Veg</span>
                    </div>
                    
                    <div className="meal-type-tabs">
                        {['breakfast', 'lunch', 'dinner'].map(type => (
                            <button 
                                key={type} 
                                className={`tab-btn ${activeMealType === type ? 'active' : ''}`}
                                onClick={() => setActiveMealType(type)}
                            >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                                {selectionCounts[type] > 0 && (
                                    <span className="selection-badge">{selectionCounts[type]}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    <div className="package-list-view">
                        {filteredPackages.length > 0 ? (
                            filteredPackages.map(pkg => (
                                <PackageItem
                                    key={pkg.id}
                                    id={pkg.id}
                                    title={pkg.name}
                                    price={pkg.price || 0}
                                    isPureVeg={pkg.isPureVeg}
                                    imageUrl={pkg.imageUrl}
                                    weeklyMenu={pkg.weeklyMenu}
                                    isSelected={selectedMeals[pkg.mealType]?.id === pkg.id}
                                    onSelect={() => handleSelectPackage(pkg)}
                                />
                            ))
                        ) : (
                            <p className="no-packages-found">No {isPureVeg ? 'Pure Veg' : ''} {activeMealType} plans available for this category.</p>
                        )}
                    </div>
                </div>

                <div className="sidebar-summary">
                    <div className="summary-card">
                        <h3 className="summary-title">Your Combo Details</h3>
                        <div className="summary-item cuisine">
                            <span>Cuisine</span>
                            <span>{category?.name}</span>
                        </div>
                        <div className="summary-item-header">MEAL COMBO</div>
                        
                        {['breakfast', 'lunch', 'dinner'].map(type => (
                            <div key={type} className="summary-item">
                                <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                                {selectedMeals[type] ? (
                                    <span className="selected-meal">{selectedMeals[type].name}</span>
                                ) : (
                                    <span className="not-selected">Not selected</span>
                                )}
                            </div>
                        ))}

                        <button className="subscribe-btn" onClick={handleSubscribe} disabled={Object.values(selectedMeals).every(m => m === null)}>
                            Select Days & Subscribe
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategoryMeals;